import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNearViewport } from '@app/utils/hooks';
import { Button } from '@ui/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/components/ui/table';

export interface SprintSectionProps {
  /** Heading above the table, e.g. "Sprint 12 (Sun, 21 June – Sun, 05 July)". */
  title: string;
  /** How many columns and rows the table will have, so the placeholder stands in at the right size. */
  columnCount: number;
  rowCount: number;
  /** Rendered once `children` is asked for and the rows are in. */
  children?: ReactNode;
  /** Whether the rows have arrived. While false the placeholder holds the space. */
  isLoaded: boolean;
  /** The rows were asked for and the request failed. */
  hasFailed?: boolean;
  /** Asks again, for the button the failure offers. */
  onRetry?: () => void;
  /** Told when the section comes near the viewport, which is when its rows are worth fetching. */
  onNear: () => void;
}

/**
 * One sprint's block in a reporting list: heading, then either its table or a placeholder the same
 * size.
 *
 * The placeholder is the point. Growing the list as data arrived meant the page got taller under
 * whoever was scrolling it — flick to the bottom and the bottom moved, leaving you stranded in the
 * middle of a list that had just doubled. Every sprint of the open year is laid out at once, so the
 * page is its final height from the start and a fast scroll lands where it was aimed. What fills in
 * afterwards fills in inside space that was already reserved for it.
 */
export const SprintSection = ({
  title,
  columnCount,
  rowCount,
  children,
  isLoaded,
  hasFailed = false,
  onRetry,
  onNear,
}: SprintSectionProps): ReactElement => {
  const { t } = useTranslation();
  const [ref, isNear] = useNearViewport<HTMLDivElement>();

  React.useEffect(() => {
    if (isNear) onNear();
    // `onNear` is a stable setter from the parent; re-running on a new identity would re-ask needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNear]);

  // A placeholder that never resolves is indistinguishable from a slow one, so a sprint that failed
  // says so and offers the ask again rather than holding its space in silence.
  const failure = (
    <div className="flex items-center justify-between gap-4 rounded-md border bg-card px-6 py-4 text-sm text-muted-foreground shadow-xs">
      <span>{t('somethingWentWrong')}</span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('retry')}
      </Button>
    </div>
  );

  const placeholder = (
    // Same primitives and row heights as DataTable, so the swap changes nothing about the layout.
    <div
      className="relative w-full overflow-auto rounded-md border bg-card text-card-foreground shadow-xs"
      aria-busy
      aria-label={t('loading')}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {Array.from({ length: columnCount }, (_, column) => (
              <TableHead key={column} className="first:pl-6 last:pr-6" />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rowCount }, (_, row) => (
            <TableRow key={row} className="h-11">
              {Array.from({ length: columnCount }, (_, column) => (
                <TableCell key={column} className="first:pl-6 last:pr-6">
                  <span className="block h-3 w-24 max-w-full animate-pulse rounded bg-muted-foreground/15" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="mb-6" ref={ref}>
      <h2 className="mb-2 text-base font-semibold text-foreground">{title}</h2>
      {isLoaded ? children : hasFailed && failure}
      {!isLoaded && !hasFailed && placeholder}
    </div>
  );
};

export default SprintSection;
